import { Box, alpha, Typography, Theme } from "@mui/material";
import { formatDueBadge } from "../../utils/taskUtils";
import { ChipSize, UIComponentSize } from "@/types/shared/uiComponents";

interface DueBadgeProps {
  /** Due date string (YYYY-MM-DD or ISO format) */
  dueDate?: string | null;
  /** Fallback text when no due date */
  emptyText?: string;
  /** Size variant */
  size?: ChipSize;
}

/**
 * Displays a due date as a colored badge with appropriate styling
 * based on urgency (overdue = red, today = yellow, future = neutral)
 */
export function DueBadge({ dueDate, emptyText = "—", size = UIComponentSize.Medium }: DueBadgeProps) {
  const { label, tone } = formatDueBadge(dueDate);

  if (!label) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  const sizeStyles =
    size === UIComponentSize.Small
      ? { px: 1, py: 0.25, fontSize: "0.6875rem" }
      : { px: 1.5, py: 0.5, fontSize: "0.75rem" };

  const toneStyles = {
    error: {
      bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.1),
      color: "error.main",
    },
    warning: {
      bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, 0.1),
      color: "warning.dark",
    },
    default: {
      bgcolor: "action.hover",
      color: "text.secondary",
    },
  };

  return (
    <Box
      component="span"
      sx={{
        ...sizeStyles,
        borderRadius: 1,
        fontWeight: 500,
        whiteSpace: "nowrap",
        ...toneStyles[tone],
      }}
    >
      {label}
    </Box>
  );
}
